import { inject, Injectable } from '@angular/core';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../state.service';
import { Bundle, ChargeItem, ImagingStudy, Invoice, Observation, Procedure, ServiceRequest } from 'fhir/r4';
import { forkJoin, map, Observable, of } from 'rxjs';
import { referenceArrayContains, referenceMatches } from '../fhir-reference.util';
import { UtilityService } from '../utility.service';

@Injectable({
    providedIn: 'root'
})
export class LabBillingPlaceholderService {
    private readonly backendEndPoint = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);
    private readonly utilityService = inject(UtilityService);

    /**
     * Same logic previously implemented in LabRequestsComponent.
     * Resolves all Observations linked to the ServiceRequest (basedOn), then selects a preferred one
     * based on ChargeItem linkage and Invoice balance.
     *
     * Uses three-tier queries for observations, charge items, and invoices with server-side
     * and client-side fallbacks.
     */
    resolveBillingAndPlaceholderForLab(
        serviceRequest: ServiceRequest,
        patientId: string
    ): Observable<{ observations: Observation[]; selectedObservation: Observation | null; chargeItem: ChargeItem | null; hasBalancedInvoice: boolean; linkedInvoices: Invoice[] }> {
        const srRef = `ServiceRequest/${serviceRequest.id}`;

        const currentPatientId = this.stateService.currentPatientId$.getValue();
        const scope: 'patient' | 'orgWide' = currentPatientId && currentPatientId === patientId ? 'patient' : 'orgWide';
        const subjectRefOr = [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`];

        // Tier-1 observation query: by basedOn reference, tier-3 fallback: patient-scoped all observations
        const obs$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Observation>({
            resourceType: 'Observation',
            urlSearchObject: {
                subject: `Patient/${patientId}`,
                'based-on': srRef
            },
            querySearchObject: [
                { key: 'basedOn.reference', value: srRef },
                // Local patient filter (OR). Note: querySearchObject filters are ANDed overall.
                { key: 'subject.reference', value: subjectRefOr }
            ],
            patientId,
            scope,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 10,
            // expectedMinLength: 1,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        // Tier-1 charge items query: by subject (patient), tier-3 fallback: all charge items
        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: `Patient/${patientId}`, status: 'billed,billable' },
            querySearchObject: [{ key: 'subject.reference', value: subjectRefOr }, 
               { key: 'status', value: ['billable', 'billed'] }
            ],
            patientId,
            scope,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        // Tier-1 invoice query: status=balanced only (server filters), tier-3 fallback: all invoices then filter locally
        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced',
                subject: `Patient/${patientId}` // need to be remved for some fhir servers that don't support subject in invoice search
             },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: subjectRefOr }
            ],
            scope: 'orgWide',
            patientId,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
        });

        return forkJoin({ observations: obs$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ observations, chargeItems, invoices }) => {
                const obsList = (observations || []) as Observation[];
                const byRef = new Map<string, Observation>();
                for (const o of obsList) {
                    if (!o?.id) continue;
                    byRef.set(`Observation/${o.id}`, o);
                }

                const isBalancedForChargeItem = (ci: any): boolean => {
                    const chargeItemRef = ci?.id ? `ChargeItem/${ci.id}` : null;
                    if (!chargeItemRef) return false;
                    return (invoices || []).some((inv: any) => {
                        const status = (inv?.status || '').toString().toLowerCase();
                        if (status !== 'balanced') return false;
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li: any) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            return ref === chargeItemRef || ref.endsWith(chargeItemRef);
                        });
                    });
                };

                const matchesAnyObservation = (ref: string): { obsRef: string; obs: Observation } | null => {
                    if (!ref) return null;
                    if (byRef.has(ref)) return { obsRef: ref, obs: byRef.get(ref)! };
                    for (const [obsRef, obs] of byRef.entries()) {
                        if (referenceMatches(ref, obsRef)) return { obsRef, obs };
                    }
                    return null;
                };

                const linkedChargeItems: Array<{ chargeItem: ChargeItem; observation: Observation; balanced: boolean }> = [];
                for (const ci of (chargeItems || []) as any[]) {
                    const services = ci?.service || [];
                    for (const s of services) {
                        const ref = (s?.reference || '').toString();
                        const match = matchesAnyObservation(ref) || (ref === srRef || referenceMatches(ref, srRef) ? { obsRef: '', obs: obsList[0] || ({} as any) } : null);
                        if (match) {
                            linkedChargeItems.push({
                                chargeItem: ci as ChargeItem,
                                observation: match.obs,
                                balanced: isBalancedForChargeItem(ci)
                            });
                            break;
                        }
                    }
                }

                console.log('Linked charge items to observations:', linkedChargeItems);

                const chargeItemRefs = new Set<string>();
                for (const ci of linkedChargeItems) {
                    if (ci?.chargeItem?.id) {
                        chargeItemRefs.add(`ChargeItem/${ci.chargeItem.id}`);
                    }
                }

                const linkedInvoices = (invoices || []).filter((inv: any) => {
                    const lineItems = inv?.lineItem || [];
                    return lineItems.some((li: any) => {
                        const ref = (li?.chargeItemReference?.reference || '').toString();
                        if (!ref) return false;
                        for (const target of chargeItemRefs) {
                            if (ref === target || ref.endsWith(target)) return true;
                        }
                        return false;
                    });
                }) as Invoice[];

                const preferredLinked = linkedChargeItems.find((x) => x.balanced) || linkedChargeItems[0] || null;

                const fallbackObservation =
                    obsList.find((o: any) => (o?.status || '').toString().toLowerCase() === 'registered') || obsList[0] || null;

                const selectedObservation = preferredLinked?.observation || fallbackObservation;
                const chargeItem = preferredLinked?.chargeItem || null;
                const hasBalancedInvoice = preferredLinked?.balanced === true;

                return { observations: obsList, selectedObservation, chargeItem, hasBalancedInvoice, linkedInvoices };
            })
        );
    }

    /**
     * Mirrors `resolveBillingAndPlaceholderForLab`, but for ImagingStudy.
     */
    resolveBillingAndPlaceholderForImagingStudy(
        serviceRequest: ServiceRequest,
        patientId: string
    ): Observable<{ imagingStudies: ImagingStudy[]; selectedImagingStudy: ImagingStudy | null; chargeItem: ChargeItem | null; hasBalancedInvoice: boolean; linkedInvoices: Invoice[] }> {
        const srRef = `ServiceRequest/${serviceRequest.id}`;

        const currentPatientId = this.stateService.currentPatientId$.getValue();
        const scope: 'patient' | 'orgWide' = currentPatientId && currentPatientId === patientId ? 'patient' : 'orgWide';
        const subjectRefOr = [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`];

        // Tier-1 imaging query: by basedOn, tier-3 fallback: patient-scoped all imaging studies
        const imaging$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ImagingStudy>({
            resourceType: 'ImagingStudy',
            urlSearchObject: { subject: `Patient/${patientId}`, 'basedon': srRef },
            querySearchObject: [
                { key: 'basedOn.reference', value: srRef },
                { key: 'subject.reference', value: subjectRefOr }
            ],
            patientId,
            scope,
            patientScopeSearchParam: 'subject',
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 10,
            // expectedMinLength: 1,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        // Tier-1 charge items query: by subject (patient)
        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: `Patient/${patientId}`, status: 'billable,billed' },
            querySearchObject: [{ key: 'subject.reference', value: subjectRefOr },  { key: 'status', value: ['billable', 'billed'] }],
            patientId,
            scope,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        // Tier-1 invoice query: status=balanced only
        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced',
                  subject: `Patient/${patientId}` // need to be remved for some fhir servers that don't support subject in invoice search
             },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: subjectRefOr }
            ],
            scope,
            patientId,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 1000,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
        });

        return forkJoin({ imagingStudies: imaging$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ imagingStudies, chargeItems, invoices }) => {
                const studies = (imagingStudies || []) as ImagingStudy[];
                const byRef = new Map<string, ImagingStudy>();
                for (const s of studies) {
                    if (!s?.id) continue;
                    byRef.set(`ImagingStudy/${s.id}`, s);
                }

                const isBalancedForChargeItem = (ci: any): boolean => {
                    const chargeItemRef = ci?.id ? `ChargeItem/${ci.id}` : null;
                    if (!chargeItemRef) return false;
                    return (invoices || []).some((inv: any) => {
                        const status = (inv?.status || '').toString().toLowerCase();
                        if (status !== 'balanced') return false;
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li: any) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            return ref === chargeItemRef || ref.endsWith(chargeItemRef);
                        });
                    });
                };

                const matchesAnyImagingStudy = (ref: string): { studyRef: string; study: ImagingStudy } | null => {
                    if (!ref) return null;
                    if (byRef.has(ref)) return { studyRef: ref, study: byRef.get(ref)! };
                    for (const [studyRef, study] of byRef.entries()) {
                        if (referenceMatches(ref, studyRef)) return { studyRef, study };
                    }
                    return null;
                };

                const linkedChargeItems: Array<{ chargeItem: ChargeItem; imagingStudy: ImagingStudy; balanced: boolean }> = [];
                for (const ci of (chargeItems || []) as any[]) {
                    const services = ci?.service || [];
                    for (const s of services) {
                        const ref = (s?.reference || '').toString();
                        const match = matchesAnyImagingStudy(ref) || (ref === srRef || referenceMatches(ref, srRef) ? { studyRef: '', study: studies[0] || ({} as any) } : null);
                        if (match) {
                            linkedChargeItems.push({
                                chargeItem: ci as ChargeItem,
                                imagingStudy: match.study,
                                balanced: isBalancedForChargeItem(ci)
                            });
                            break;
                        }
                    }
                }

                const chargeItemRefs = new Set<string>();
                for (const ci of linkedChargeItems) {
                    if (ci?.chargeItem?.id) {
                        chargeItemRefs.add(`ChargeItem/${ci.chargeItem.id}`);
                    }
                }

                const linkedInvoices = (invoices || []).filter((inv: any) => {
                    const lineItems = inv?.lineItem || [];
                    return lineItems.some((li: any) => {
                        const ref = (li?.chargeItemReference?.reference || '').toString();
                        if (!ref) return false;
                        for (const target of chargeItemRefs) {
                            if (ref === target || ref.endsWith(target)) return true;
                        }
                        return false;
                    });
                }) as Invoice[];

                const preferredLinked = linkedChargeItems.find((x) => x.balanced) || linkedChargeItems[0] || null;
                const fallbackStudy =
                    studies.find((s: any) => (s?.status || '').toString().toLowerCase() === 'available') || studies[0] || null;

                const selectedImagingStudy = preferredLinked?.imagingStudy || fallbackStudy;
                const chargeItem = preferredLinked?.chargeItem || null;
                const hasBalancedInvoice = linkedInvoices.some((inv: any) => ((inv?.status || '').toString().toLowerCase() === 'balanced'));

                return { imagingStudies: studies, selectedImagingStudy, chargeItem, hasBalancedInvoice, linkedInvoices };
            })
        );
    }

    /**
     * Mirrors `resolveBillingAndPlaceholderForLab`, but for Procedure.
     */
    resolveBillingAndPlaceholderForProcedure(
        serviceRequest: ServiceRequest,
        patientId: string
    ): Observable<{ procedures: Procedure[]; selectedProcedure: Procedure | null; chargeItem: ChargeItem | null; hasBalancedInvoice: boolean; linkedInvoices: Invoice[] }> {
        const srRef = `ServiceRequest/${serviceRequest.id}`;

        const currentPatientId = this.stateService.currentPatientId$.getValue();
        const scope: 'patient' | 'orgWide' = currentPatientId && currentPatientId === patientId ? 'patient' : 'orgWide';
        const subjectRefOr = [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`];

        // Tier-1 procedure query: by basedOn, tier-3 fallback: patient-scoped all procedures
        const procedures$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Procedure>({
            resourceType: 'Procedure',
            urlSearchObject: { subject: `Patient/${patientId}`, 'based-on': srRef },
            querySearchObject: [
                { key: 'basedOn.reference', value: srRef },
                { key: 'subject.reference', value: subjectRefOr }
            ],
            patientId,
            scope,
            patientScopeSearchParam: 'subject',
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 10,
            // expectedMinLength: 1,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        // Tier-1 charge items query: by subject (patient)
        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: `Patient/${patientId}`, status: 'billable,billed' },
            querySearchObject: [{ key: 'subject.reference', value: subjectRefOr },  { key: 'status', value: ['billable', 'billed'] }],
            patientId,
            scope,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        // Tier-1 invoice query: status=balanced only
        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced',
                  subject: `Patient/${patientId}` // need to be remved for some fhir servers that don't support subject in invoice search
             },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: subjectRefOr }
            ],
            scope,
            patientId,
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 1000,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
        });

        return forkJoin({ procedures: procedures$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ procedures, chargeItems, invoices }) => {
                const procList = (procedures || []) as Procedure[];
                const byRef = new Map<string, Procedure>();
                for (const p of procList) {
                    if (!p?.id) continue;
                    byRef.set(`Procedure/${p.id}`, p);
                }

                const isBalancedForChargeItem = (ci: any): boolean => {
                    const chargeItemRef = ci?.id ? `ChargeItem/${ci.id}` : null;
                    if (!chargeItemRef) return false;
                    return (invoices || []).some((inv: any) => {
                        const status = (inv?.status || '').toString().toLowerCase();
                        if (status !== 'balanced') return false;
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li: any) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            return ref === chargeItemRef || ref.endsWith(chargeItemRef);
                        });
                    });
                };

                const matchesAnyProcedure = (ref: string): { procRef: string; proc: Procedure } | null => {
                    if (!ref) return null;
                    if (byRef.has(ref)) return { procRef: ref, proc: byRef.get(ref)! };
                    for (const [procRef, proc] of byRef.entries()) {
                        if (referenceMatches(ref, procRef)) return { procRef, proc };
                    }
                    return null;
                };

                const linkedChargeItems: Array<{ chargeItem: ChargeItem; procedure: Procedure; balanced: boolean }> = [];
                for (const ci of (chargeItems || []) as any[]) {
                    const services = ci?.service || [];
                    for (const s of services) {
                        const ref = (s?.reference || '').toString();
                        const match = matchesAnyProcedure(ref) || (ref === srRef || referenceMatches(ref, srRef) ? { procRef: '', proc: procList[0] || ({} as any) } : null);
                        if (match) {
                            linkedChargeItems.push({
                                chargeItem: ci as ChargeItem,
                                procedure: match.proc,
                                balanced: isBalancedForChargeItem(ci)
                            });
                            break;
                        }
                    }
                }

                const chargeItemRefs = new Set<string>();
                for (const ci of linkedChargeItems) {
                    if (ci?.chargeItem?.id) {
                        chargeItemRefs.add(`ChargeItem/${ci.chargeItem.id}`);
                    }
                }

                const linkedInvoices = (invoices || []).filter((inv: any) => {
                    const lineItems = inv?.lineItem || [];
                    return lineItems.some((li: any) => {
                        const ref = (li?.chargeItemReference?.reference || '').toString();
                        if (!ref) return false;
                        for (const target of chargeItemRefs) {
                            if (ref === target || ref.endsWith(target)) return true;
                        }
                        return false;
                    });
                }) as Invoice[];

                const preferredLinked = linkedChargeItems.find((x) => x.balanced) || linkedChargeItems[0] || null;
                // A finalized Procedure is the report of record. Prefer it over a
                // draft that may happen to be linked to the ChargeItem.
                const finalizedProcedure = procList
                    .filter((p: any) => (p?.status || '').toString().toLowerCase() === 'completed')
                    .sort((a: any, b: any) => {
                        const aTime = new Date(a?.performedDateTime || a?.meta?.lastUpdated || 0).getTime();
                        const bTime = new Date(b?.performedDateTime || b?.meta?.lastUpdated || 0).getTime();
                        return bTime - aTime;
                    })[0] || null;
                const fallbackProcedure = finalizedProcedure || procList[0] || null;

                const selectedProcedure = finalizedProcedure || preferredLinked?.procedure || fallbackProcedure;
                const chargeItem = preferredLinked?.chargeItem || null;
                const hasBalancedInvoice = preferredLinked?.balanced === true;

                return { procedures: procList, selectedProcedure, chargeItem, hasBalancedInvoice, linkedInvoices };
            })
        );
    }

    batchResolveBillingAndPlaceholderForLab(
        serviceRequests: ServiceRequest[]
    ): Observable<Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }>> {
        const list = (serviceRequests || []).filter((sr) => !!sr.id);
        if (!list.length) return of({});

        const patientIds = new Set<string>();
        for (const sr of list) {
            const patientRef = sr.subject?.reference || '';
            const patientId = patientRef.split('/').pop() || '';
            if (patientId) {
                patientIds.add(patientId);
                patientIds.add(`${this.backendEndPoint}/Patient/${patientId}`);
            }
        }
        const patientIdList = Array.from(patientIds);

        const scope: 'patient' | 'orgWide' = 'orgWide';

        const obs$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Observation>({
            resourceType: 'Observation',
            urlSearchObject: {
                subject: { or: patientIdList },
                'based-on': { or: list.map((sr) => `ServiceRequest/${sr.id}`) }
            },
            querySearchObject: [
                { key: 'basedOn.reference', value: list.map((sr) => `ServiceRequest/${sr.id}`) },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope,
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: { or: patientIdList }, status: 'billed,billable' },
            querySearchObject: [{ key: 'subject.reference', value: patientIdList },  { key: 'status', value: ['billable', 'billed'] }],
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
            scope,
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced', subject: { or: patientIdList } },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope: 'orgWide',
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        return forkJoin({ observations: obs$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ observations, chargeItems, invoices }) => {
                const obsList = (observations || []) as Observation[];
                const allChargeItems = (chargeItems || []) as ChargeItem[];
                const allInvoices = (invoices || []) as Invoice[];

                const result: Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> = {};

                for (const sr of list) {
                    const srRef = `ServiceRequest/${sr.id}`;

                    const myObs = obsList.filter((o) => {
                        const basedOn = Array.isArray(o?.basedOn) ? o.basedOn : [];
                        return basedOn.some((b) => {
                            const ref = (b?.reference || '').toString();
                            return ref === srRef || referenceMatches(ref, srRef);
                        });
                    });

                    const obsRefs = new Set(myObs.filter((o) => !!o.id).map((o) => `Observation/${o.id}`));
                    const myChargeItems = allChargeItems.filter((ci) => {
                        const services = ci?.service || [];
                        return services.some((s) => {
                            const ref = (s?.reference || '').toString();
                            if (!ref) return false;
                            if (ref === srRef || referenceMatches(ref, srRef)) return true;
                            for (const target of obsRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const chargeItemRefs = new Set(myChargeItems.filter((ci) => !!ci.id).map((ci) => `ChargeItem/${ci.id}`));
                    const myInvoices = allInvoices.filter((inv) => {
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            if (!ref) return false;
                            for (const target of chargeItemRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const hasBalancedInvoice = myInvoices.some((inv) => (inv?.status || '').toString().toLowerCase() === 'balanced');

                    result[sr.id!] = {
                        chargeItems: myChargeItems,
                        invoices: myInvoices,
                        hasBalancedInvoice
                    };
                }

                return result;
            })
        );
    }

    batchResolveBillingAndPlaceholderForImagingStudy(
        serviceRequests: ServiceRequest[]
    ): Observable<Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }>> {
        const list = (serviceRequests || []).filter((sr) => !!sr.id);
        if (!list.length) return of({});

        const patientIds = new Set<string>();
        for (const sr of list) {
            const patientRef = sr.subject?.reference || '';
            const patientId = patientRef.split('/').pop() || '';
            if (patientId) {
                patientIds.add(patientId);
                patientIds.add(`${this.backendEndPoint}/Patient/${patientId}`);
            }
        }
        const patientIdList = Array.from(patientIds);

        const scope: 'patient' | 'orgWide' = 'orgWide';

        const imaging$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ImagingStudy>({
            resourceType: 'ImagingStudy',
            urlSearchObject: {
                subject: { or: patientIdList },
                'based-on': { or: list.map((sr) => `ServiceRequest/${sr.id}`) }
            },
            querySearchObject: [
                { key: 'basedOn.reference', value: list.map((sr) => `ServiceRequest/${sr.id}`) },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope,
            patientScopeSearchParam: 'subject',
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: { or: patientIdList }, status: 'billed,billable' },
            querySearchObject: [{ key: 'subject.reference', value: patientIdList },  { key: 'status', value: ['billable', 'billed'] }],
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
            scope,
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced', subject: { or: patientIdList } },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope: 'orgWide',
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        return forkJoin({ imagingStudies: imaging$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ imagingStudies, chargeItems, invoices }) => {
                const studiesList = (imagingStudies || []) as ImagingStudy[];
                const allChargeItems = (chargeItems || []) as ChargeItem[];
                const allInvoices = (invoices || []) as Invoice[];

                const result: Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> = {};

                for (const sr of list) {
                    const srRef = `ServiceRequest/${sr.id}`;

                    const myStudies = studiesList.filter((s) => {
                        const basedOn = Array.isArray(s?.basedOn) ? s.basedOn : [];
                        return basedOn.some((b) => {
                            const ref = (b?.reference || '').toString();
                            return ref === srRef || referenceMatches(ref, srRef);
                        });
                    });

                    const studyRefs = new Set(myStudies.filter((s) => !!s.id).map((s) => `ImagingStudy/${s.id}`));
                    const myChargeItems = allChargeItems.filter((ci) => {
                        const services = ci?.service || [];
                        return services.some((s) => {
                            const ref = (s?.reference || '').toString();
                            if (!ref) return false;
                            if (ref === srRef || referenceMatches(ref, srRef)) return true;
                            for (const target of studyRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const chargeItemRefs = new Set(myChargeItems.filter((ci) => !!ci.id).map((ci) => `ChargeItem/${ci.id}`));
                    const myInvoices = allInvoices.filter((inv) => {
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            if (!ref) return false;
                            for (const target of chargeItemRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const hasBalancedInvoice = myInvoices.some((inv) => (inv?.status || '').toString().toLowerCase() === 'balanced');

                    result[sr.id!] = {
                        chargeItems: myChargeItems,
                        invoices: myInvoices,
                        hasBalancedInvoice
                    };
                }

                return result;
            })
        );
    }

    batchResolveBillingAndPlaceholderForProcedure(
        serviceRequests: ServiceRequest[]
    ): Observable<Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }>> {
        const list = (serviceRequests || []).filter((sr) => !!sr.id);
        if (!list.length) return of({});

        const patientIds = new Set<string>();
        for (const sr of list) {
            const patientRef = sr.subject?.reference || '';
            const patientId = patientRef.split('/').pop() || '';
            if (patientId) {
                patientIds.add(patientId);
                patientIds.add(`${this.backendEndPoint}/Patient/${patientId}`);
            }
        }
        const patientIdList = Array.from(patientIds);

        const scope: 'patient' | 'orgWide' = 'orgWide';

        const procedures$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Procedure>({
            resourceType: 'Procedure',
            urlSearchObject: {
                subject: { or: patientIdList },
                'based-on': { or: list.map((sr) => `ServiceRequest/${sr.id}`) }
            },
            querySearchObject: [
                { key: 'basedOn.reference', value: list.map((sr) => `ServiceRequest/${sr.id}`) },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope,
            patientScopeSearchParam: 'subject',
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { subject: { or: patientIdList }, status: 'billed,billable' },
            querySearchObject: [{ key: 'subject.reference', value: patientIdList },  { key: 'status', value: ['billable', 'billed'] }],
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
            scope,
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100
        });

        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
            resourceType: 'Invoice',
            urlSearchObject: { status: 'balanced', subject: { or: patientIdList } },
            querySearchObject: [
                { key: 'status', value: 'balanced' },
                { key: 'subject.reference', value: patientIdList }
            ],
            scope: 'orgWide',
            count: 10000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 100,
            expectedMinLength: 0,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
        });

        return forkJoin({ procedures: procedures$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
            map(({ procedures, chargeItems, invoices }) => {
                const procList = (procedures || []) as Procedure[];
                const allChargeItems = (chargeItems || []) as ChargeItem[];
                const allInvoices = (invoices || []) as Invoice[];

                const result: Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> = {};

                for (const sr of list) {
                    const srRef = `ServiceRequest/${sr.id}`;

                    const myProcs = procList.filter((p) => {
                        const basedOn = Array.isArray(p?.basedOn) ? p.basedOn : [];
                        return basedOn.some((b) => {
                            const ref = (b?.reference || '').toString();
                            return ref === srRef || referenceMatches(ref, srRef);
                        });
                    });

                    const procRefs = new Set(myProcs.filter((p) => !!p.id).map((p) => `Procedure/${p.id}`));
                    const myChargeItems = allChargeItems.filter((ci) => {
                        const services = ci?.service || [];
                        return services.some((s) => {
                            const ref = (s?.reference || '').toString();
                            if (!ref) return false;
                            if (ref === srRef || referenceMatches(ref, srRef)) return true;
                            for (const target of procRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const chargeItemRefs = new Set(myChargeItems.filter((ci) => !!ci.id).map((ci) => `ChargeItem/${ci.id}`));
                    const myInvoices = allInvoices.filter((inv) => {
                        const lineItems = inv?.lineItem || [];
                        return lineItems.some((li) => {
                            const ref = (li?.chargeItemReference?.reference || '').toString();
                            if (!ref) return false;
                            for (const target of chargeItemRefs) {
                                if (ref === target || ref.endsWith(target)) return true;
                            }
                            return false;
                        });
                    });

                    const hasBalancedInvoice = myInvoices.some((inv) => (inv?.status || '').toString().toLowerCase() === 'balanced');

                    result[sr.id!] = {
                        chargeItems: myChargeItems,
                        invoices: myInvoices,
                        hasBalancedInvoice
                    };
                }

                return result;
            })
        );
    }
}
